/**
 * "Try the new job page" — without forcing anyone over (Corbin 11/4: "don't
 * force the switch just make a new headline that has the date and says to try
 * it out and give a comments section for it so they can describe what's
 * missing and we get that response").
 *
 *  - TryNewPageBanner: a dated headline at the top of the CLASSIC job page.
 *    "Try it" flips the person's own switch (lib/finish-line.ts, the same
 *    preference the top-right toggle uses). "Not now" hides it for good, on
 *    every computer. Inline, never a pop-up, no bell, no push.
 *  - Someone who tried the new page and came back (or opened the classic page
 *    once from the new one) sees "What was missing?" instead, once.
 *  - JobPageFeedbackBox: "What's missing?" — a short box + Send. Each answer
 *    files a Help Desk case tagged new_job_page_feedback with the person, the
 *    job and their words (POST /api/jobs/:id/page-feedback). The new job page
 *    carries the same box.
 */
import { useEffect, useState } from "react";
import { Check, MessageSquareText, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useFinishLineSwitch } from "@/lib/finish-line";
import { useUserPreference } from "@/lib/user-preference";
import { cn } from "@/lib/utils";

/** The headline's date. A new headline gets a new date AND a new key, so it shows again once. */
export const TRY_BANNER_DATE = "Nov 4";
export const TRY_BANNER_KEY = "job-page.try-banner-2026-11-04";
/** Set the first time someone opens the new job page. */
export const TRIED_NEW_KEY = "job-page.tried-new";

/** Which card the classic page shows. `dismissed` is the card they said "Not now" to. */
export type TryBannerMode = "try" | "why" | "escaped";
export function tryBannerMode(opts: { switchOn: boolean; triedNew: boolean }): TryBannerMode {
    if (opts.switchOn) return "escaped";
    return opts.triedNew ? "why" : "try";
}
/**
 * `dismissed` is the card they last closed. "Not now" on the try card doesn't
 * hide the one-time "what was missing?" after they do try it; each card is
 * closed on its own.
 */
export function tryBannerHidden(mode: TryBannerMode, dismissed: string | null): boolean {
    if (!dismissed) return false;
    if (mode === "try") return true;
    return dismissed === mode;
}

/** The new job page calls this once: it marks "has tried the new page". */
export function useMarkTriedNew(enabled: boolean): void {
    const [tried, setTried, { loaded }] = useUserPreference<boolean>(TRIED_NEW_KEY, false);
    useEffect(() => {
        if (enabled && loaded && !tried) setTried(true);
    }, [enabled, loaded, tried, setTried]);
}

export function JobPageFeedbackBox({
    jobId,
    page,
    label = "What's missing on the new job page?",
    hint = "Tell us what you couldn't do here. We read every one.",
    className,
    onSent,
}: {
    jobId: string;
    page: "new" | "classic";
    label?: string;
    hint?: string;
    className?: string;
    onSent?: () => void;
}) {
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const send = async () => {
        if (!text.trim() || sending) return;
        setSending(true);
        try {
            await api.post(`/api/jobs/${jobId}/page-feedback`, { text: text.trim(), page });
            setSent(true);
            setText("");
            onSent?.();
        } catch {
            toast.error("Couldn't send that. Try again in a minute.");
        } finally {
            setSending(false);
        }
    };
    const fieldId = `page-feedback-${page}`;
    return (
        <div className={cn("grid gap-1.5", className)} data-testid={`page-feedback-${page}`}>
            <label htmlFor={fieldId} className="flex items-center gap-1.5 text-[15px] font-bold">
                <MessageSquareText className="h-4 w-4 text-primary" aria-hidden /> {label}
            </label>
            {sent ? (
                <p
                    className="m-0 flex items-center gap-1.5 rounded-lg bg-emerald-50 px-3 py-2 text-[14px] font-semibold text-emerald-800 dark:bg-emerald-500/10 dark:text-emerald-300"
                    role="status"
                    data-testid={`page-feedback-${page}-sent`}
                >
                    <Check className="h-4 w-4" aria-hidden /> Got it. Thank you.{" "}
                    <button
                        type="button"
                        className="ml-1 font-semibold underline underline-offset-2"
                        onClick={() => setSent(false)}
                    >
                        Add another
                    </button>
                </p>
            ) : (
                <>
                    <p className="m-0 text-[13px] text-muted-foreground">{hint}</p>
                    <div className="flex flex-wrap items-end gap-2">
                        <textarea
                            id={fieldId}
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            rows={2}
                            maxLength={4000}
                            placeholder="Example: I couldn't attach the city report."
                            className="min-h-[56px] min-w-[220px] flex-1 resize-y rounded-lg border border-input bg-background px-3 py-2 text-[15px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            data-testid={`page-feedback-${page}-text`}
                        />
                        <button
                            type="button"
                            onClick={send}
                            disabled={!text.trim() || sending}
                            className="inline-flex min-h-[40px] items-center rounded-lg bg-primary px-4 text-[14px] font-bold text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                            data-testid={`page-feedback-${page}-send`}
                        >
                            {sending ? "Sending…" : "Send"}
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}

const WORDS: Record<TryBannerMode, { title: string; body: string }> = {
    try: {
        title: `New job page: ready to try (${TRY_BANNER_DATE})`,
        body: "Same job, simpler page. Only your screen changes, and you can switch back any time, top right.",
    },
    why: {
        title: "You went back to the classic page",
        body: "What was missing on the new page? Tell us and we'll add it.",
    },
    escaped: {
        title: "What couldn't you do on the new page?",
        body: "You opened the classic page for this job. Tell us what for, so the new page can do it too.",
    },
};

export function TryNewPageBanner({ jobId }: { jobId: string | undefined }) {
    const sw = useFinishLineSwitch();
    const [triedNew, , triedState] = useUserPreference<boolean>(TRIED_NEW_KEY, false);
    const [dismissed, setDismissed, dismissedState] = useUserPreference<string | null>(TRY_BANNER_KEY, null);
    const mode = tryBannerMode({ switchOn: sw.on, triedNew: triedNew === true });
    const [showBox, setShowBox] = useState(false);
    // After "Send" on the one-time "what was missing?" card it is done for good,
    // but stays on screen this visit so they see "Got it".
    const [justSent, setJustSent] = useState(false);
    if (!sw.allowed || !jobId || !triedState.loaded || !dismissedState.loaded) return null;
    if (!justSent && tryBannerHidden(mode, dismissed)) return null;
    const words = WORDS[mode];
    const boxOpen = mode !== "try" || showBox;
    return (
        <section
            className="rounded-2xl border border-primary/30 bg-primary/5 px-4 py-3 text-card-foreground"
            data-testid={`try-banner-${mode}`}
            aria-label={words.title}
        >
            <div className="flex items-start gap-3">
                <span
                    className="grid h-10 w-10 flex-none place-items-center rounded-xl bg-primary text-primary-foreground"
                    aria-hidden
                >
                    <Sparkles className="h-5 w-5" />
                </span>
                <div className="min-w-0 flex-1">
                    <h2 className="m-0 text-[17px] font-extrabold leading-snug">{words.title}</h2>
                    <p className="m-0 text-[15px] text-muted-foreground">{words.body}</p>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                        {mode === "try" && (
                            <button
                                type="button"
                                className="inline-flex min-h-[40px] items-center gap-1.5 rounded-lg bg-primary px-4 text-[14px] font-bold text-primary-foreground hover:bg-primary/90"
                                onClick={() => {
                                    sw.set(true);
                                    toast.success(
                                        "New job page on. Only your screen changed. Switch back any time, top right.",
                                    );
                                }}
                                data-testid="try-banner-try-it"
                            >
                                <Sparkles className="h-4 w-4" aria-hidden /> Try it
                            </button>
                        )}
                        <button
                            type="button"
                            className="inline-flex min-h-[40px] items-center rounded-lg border border-border bg-card px-3 text-[14px] font-semibold hover:bg-muted"
                            onClick={() => setDismissed(mode)}
                            data-testid="try-banner-not-now"
                        >
                            Not now
                        </button>
                        {mode === "try" && !showBox && (
                            <button
                                type="button"
                                className="inline-flex min-h-[40px] items-center gap-1.5 px-2 text-[14px] font-semibold text-primary underline-offset-2 hover:underline"
                                onClick={() => setShowBox(true)}
                                data-testid="try-banner-tell-us"
                            >
                                <MessageSquareText className="h-4 w-4" aria-hidden /> Tell us what's missing
                            </button>
                        )}
                    </div>
                </div>
            </div>
            {boxOpen && (
                <JobPageFeedbackBox
                    jobId={jobId}
                    page="classic"
                    label={mode === "try" ? "What's missing on the new job page?" : "What was missing?"}
                    hint="Tell us what you couldn't do on the new page. We read every one."
                    className="mt-3 border-t border-primary/20 pt-3"
                    onSent={() => {
                        if (mode !== "why") return;
                        setJustSent(true);
                        setDismissed("why");
                    }}
                />
            )}
        </section>
    );
}
